import React from "react";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import EditIcon from "@mui/icons-material/Edit";
import FilterListIcon from "@mui/icons-material/FilterList";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { TrainingEmptyState } from "./TrainingEmptyState";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getRightworkAPI } from "../requests/utilities/requests";
import { formatDate } from "../requests/utilities/helpers";
import { API_get_learning_roleplay_summaries } from "../../../constants";
import LibraryFilterChips from "./LibraryFilterChips";

const ASSIGNABLE_OPTIONS = [
  { value: "assignable", label: "Assignable" },
  { value: "not_assignable", label: "Not assignable" },
];

const filterSelectSx = {
  height: 42,
  minWidth: 170,
  borderRadius: "8px",
  backgroundColor: "white",
  fontSize: 14,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a78bfa" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
  "& .MuiSelect-select": {
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
  },
};

function RoleplayCard({ roleplay, onEdit }) {
  const criteriaCount = roleplay.criteria_count || 0;
  const updatedLabel = roleplay.updated_at
    ? `Updated ${formatDate(roleplay.updated_at)}`
    : null;
  const coverUrl = roleplay.cover?.url || null;

  return (
    <div
      className="group relative cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-200 hover:border-slate-300 hover:shadow-md"
      onClick={onEdit}
    >
      {/* Cover region — always present so every card is the same shape
          (mirrors the swipe-deck tile): the violet gradient is the backdrop,
          doubling as the no-cover placeholder with the scenario icon. */}
      <div
        className="relative h-44 overflow-hidden"
        style={{ background: "linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)" }}
      >
        {coverUrl ? (
          <img
            src={coverUrl}
            alt=""
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/70">
            <ConnectWithoutContactIcon style={{ fontSize: 44 }} />
          </div>
        )}
      </div>

      {/* Hover Edit action, overlaid on the cover corner. */}
      <div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100">
        <Tooltip title="Edit practice scenario">
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
            sx={{
              backgroundColor: "rgba(255,255,255,0.9)",
              color: "#475569",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <EditIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </div>

      {/* Body */}
      <div className="p-4">
        <h3 className="line-clamp-2 text-sm font-semibold text-slate-900">
          {roleplay.name || "Untitled practice scenario"}
        </h3>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
          <span>
            {criteriaCount} {criteriaCount === 1 ? "criterion" : "criteria"}
          </span>
          <span>·</span>
          <span>Up to {roleplay.max_turns} replies</span>
        </div>

        {roleplay.description && (
          <p className="mt-2 line-clamp-2 text-xs text-slate-500">
            {roleplay.description}
          </p>
        )}

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 ring-1 ring-violet-100">
              AI-coached
            </span>
            {roleplay.is_directly_assignable && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100">
                Assignable
              </span>
            )}
          </div>
          {updatedLabel && (
            <span className="shrink-0 text-xs text-slate-400">{updatedLabel}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function RoleplaysDisplay(props) {
  const { homeCompany, onCreate, onEdit } = props;
  const [roleplays, setRoleplays] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState("");
  const [assignableFilter, setAssignableFilter] = React.useState("all");
  const [sortKey, setSortKey] = React.useState("recent");

  const fetchRoleplays = React.useCallback(async () => {
    if (!homeCompany) return;
    setIsLoading(true);
    try {
      const res = await getRightworkAPI(API_get_learning_roleplay_summaries, {
        company_id: homeCompany,
      });
      setRoleplays(res.data?.redux?.payload?.learning_roleplays || []);
    } catch (err) {
      toast.error("Couldn't load practice scenarios. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [homeCompany]);

  React.useEffect(() => {
    fetchRoleplays();
  }, [fetchRoleplays]);

  const filteredRoleplays = React.useMemo(() => {
    const list = roleplays || [];
    const searchParts = searchInput.toLowerCase().split(" ").filter(Boolean);
    const filtered = list.filter((roleplay) => {
      const haystack = `${roleplay.name || ""} ${roleplay.description || ""}`.toLowerCase();
      if (!searchParts.every((part) => haystack.includes(part))) return false;
      if (assignableFilter === "assignable" && !roleplay.is_directly_assignable) {
        return false;
      }
      if (assignableFilter === "not_assignable" && roleplay.is_directly_assignable) {
        return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortKey === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }
      if (sortKey === "criteria") {
        return (b.criteria_count || 0) - (a.criteria_count || 0);
      }
      return (b.updated_at || b.created_at || 0) - (a.updated_at || a.created_at || 0);
    });
  }, [roleplays, searchInput, assignableFilter, sortKey]);

  const activeFilterCount = assignableFilter !== "all" ? 1 : 0;
  const hasSearchOrFilters = Boolean(searchInput || activeFilterCount);

  const clearSearchAndFilters = () => {
    setSearchInput("");
    setAssignableFilter("all");
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="mb-1 text-2xl font-bold text-slate-900">Practice Scenarios</h1>
              <p className="text-sm text-slate-600">
                Create AI practice scenarios employees can rehearse for coaching feedback
              </p>
            </div>
            <Tooltip title="Create new practice scenario">
              <button
                type="button"
                onClick={() => onCreate && onCreate()}
                className="flex items-center gap-2 rounded-lg bg-violet-700 px-6 py-3 text-white shadow-sm transition-colors hover:bg-violet-800"
              >
                <AddIcon style={{ fontSize: "20px" }} />
                <span className="font-medium">New Practice Scenario</span>
              </button>
            </Tooltip>
          </div>

          <div className="mb-6 flex items-center gap-6 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <ConnectWithoutContactIcon style={{ fontSize: "18px" }} />
              <span>
                {filteredRoleplays.length}{" "}
                {filteredRoleplays.length === 1 ? "Practice Scenario" : "Practice Scenarios"}
              </span>
            </div>
            {searchInput && (
              <div className="flex items-center gap-2 text-violet-600">
                <SearchIcon style={{ fontSize: "16px" }} />
                <span>Showing search results</span>
              </div>
            )}
            {activeFilterCount > 0 && (
              <div className="flex items-center gap-2 text-violet-600">
                <FilterListIcon style={{ fontSize: "16px" }} />
                <span>
                  {activeFilterCount}{" "}
                  {activeFilterCount === 1 ? "filter" : "filters"} active
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-md">
              <SearchIcon
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                style={{ fontSize: "20px" }}
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search practice scenarios..."
                className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-4 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100"
              />
            </div>

            <Select
              size="small"
              sx={filterSelectSx}
              value={sortKey}
              onChange={(event) => setSortKey(event.target.value)}
            >
              <MenuItem value="recent" sx={{ fontSize: 14 }}>
                Recently updated
              </MenuItem>
              <MenuItem value="name" sx={{ fontSize: 14 }}>
                Name
              </MenuItem>
              <MenuItem value="criteria" sx={{ fontSize: 14 }}>
                Most criteria
              </MenuItem>
            </Select>
          </div>

          <div className="mt-4">
            <LibraryFilterChips
              groups={[
                {
                  key: "assignable",
                  value: assignableFilter,
                  onChange: setAssignableFilter,
                  options: ASSIGNABLE_OPTIONS,
                },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="px-6 py-8">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <LoadSpinner />
          </div>
        ) : filteredRoleplays.length === 0 ? (
          <TrainingEmptyState
            icon={ConnectWithoutContactIcon}
            plural="practice scenarios"
            blurb="Build practice conversations employees rehearse with an AI character and get coaching feedback on."
            createLabel="Create your first practice scenario"
            searching={hasSearchOrFilters}
            noResultsTitle="No practice scenarios match your filters"
            noResultsBlurb="Try adjusting your search or clearing the selected filters."
            clearLabel="Clear filters"
            onClear={hasSearchOrFilters ? clearSearchAndFilters : undefined}
            onCreate={() => onCreate && onCreate()}
          />
        ) : (
          // Desktop-first breakpoints (reversed in this codebase), mirroring
          // the swipe-deck grid: 3 across on desktop, stepping down when narrow.
          <div className="grid grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 gap-6">
            {filteredRoleplays.map((roleplay) => (
              <RoleplayCard
                key={roleplay.uuid}
                roleplay={roleplay}
                onEdit={() => onEdit && onEdit(roleplay)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default RoleplaysDisplay;
